/**
 * DashboardAlbumCard — Phase C1 rewrite.
 *
 * Uses the shared CollectionAlbumSnapshotCard so every dashboard row
 * follows the canonical title hierarchy (official title large, cover
 * label small) and the same compact horizontal presentation as My
 * Collection and Hall of Fame.
 *
 * The dashboard-summary payload keeps its historical `title` (variant)
 * and `subtitle` (release) fields for compatibility. Presentation is
 * derived exclusively through `buildAlbumPresentation()` which reads
 * `subtitle` as the canonical release name and `cover_label_override`
 * / `title` as the variant descriptor — never the reverse.
 *
 * Progress % and per-card counts are intentionally left as-is: Phase
 * C1 is a visual consistency pass, not a normalization pass (that
 * work belongs to Phase C2 per user directive).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ImageOff } from "lucide-react";
import CollectionAlbumSnapshotCard from "@/components/collector/CollectionAlbumSnapshotCard";

export default function DashboardAlbumCard({ card }) {
  const nav = useNavigate();
  return (
    <div
      data-testid={`dash-album-${card.id}`}
      title={card.title}
    >
      <CollectionAlbumSnapshotCard
        album={card}
        layout="horizontal"
        showCounts={false}
        onOpen={() => nav(`/dashboard/my-collection/albums/${card.id}`)}
      />
    </div>
  );
}

/**
 * CoverBox — legacy helper still consumed by SpecialCollectibleCard.
 * Retained to avoid churning non-album surfaces during Phase C1.
 */
export function CoverBox({ url, title }) {
  return (
    <div className="relative aspect-[4/3] bg-[var(--panini-yellow-soft)] border-b-2 border-[var(--ink)] overflow-hidden">
      {url ? (
        <img
          src={url}
          alt={title ? `${title} cover art` : ""}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      ) : (
        <div className="w-full h-full grid place-items-center text-[var(--smoke)]" aria-hidden>
          <div className="flex flex-col items-center gap-1">
            <ImageOff size={20} />
            <span className="text-[10px] uppercase tracking-widest">No cover</span>
          </div>
        </div>
      )}
    </div>
  );
}
